import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import {
  AssetDTO,
  CheckStatus,
  EntityRefUtil,
  formatManuallyWithUserTimezone,
  GetCheckResponseDTO,
  GetChecksByAssetResponseDTO,
  GetWorkOrdersResponseDTO,
  IssueDTO,
  IssueSource,
  IssueStatus,
  RecordState,
  TagEntity,
  WorkOrderPriority,
  WorkOrderStatus,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { TooltipArrow, TooltipPortal } from '@radix-ui/react-tooltip';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { startCase } from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { SingleRecordPdfControls } from '@/components/single-record-pdf-controls';
import { TagBadges } from '@/components/table-page/columns/tag-badges';
import { AsyncSelect, SELECT_STYLES } from '@/components/ui/async-select';
import { Badge } from '@/components/ui/badge';
import { DatePicker } from '@/components/ui/date-picker';
import { Form, FormControl, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AsyncMultiSelect, CreatableMultiSelect } from '@/components/ui/multi-select';
import { ReadOnlyField } from '@/components/ui/read-only-field';
import { Select } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toaster';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { usePermissions } from '@/hooks/use-permissions';
import { useSaveChangesGuard } from '@/hooks/use-save-changes-guard';
import { loadUserOptions } from '@/lib/user-options';
import { cn, initials } from '@/lib/utils';
import { getTagsQueryOptions } from '@/queries/assets';
import { awaWorkOrderKeys, getWorkOrderActionsInfiniteQueryOptions, getWorkOrderByIdQueryOptions, workOrderKeys } from '@/queries/work-orders';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';
import { getWorkspaceRoute } from '@/utils/routes';
import { getWorkOrderPriorityColorClass } from '@/utils/work-order';

import { ActivityTimeline } from '../activity/activity-timeline';
import CheckDrawer from '../checks/check-drawer';
import { ArchiveConfirmationDialog } from '../common/archive-confirmation-dialog';
import { DeleteConfirmationDialog } from '../common/delete-confirmation-dialog';
import { RemoveConfirmationDialog } from '../common/remove-confirmation-dialog';
import { RestoreConfirmationDialog } from '../common/restore-confirmation-dialog';
import { SaveChangesDialog } from '../common/save-changes-dialog';
import ActivityIcon from '../icons/activity';
import ArchiveIcon from '../icons/archive';
import CloseIcon from '../icons/close';
import DeleteIcon from '../icons/delete';
import PlusIcon from '../icons/plus';
import UndoIcon from '../icons/undo';
import WarningIcon from '../icons/warning';
import IssueDrawer from '../issues/issue-drawer';
import { TablePageHeaderActionButton } from '../table-page/table-page-header';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Skeleton } from '../ui/skeleton';
import { TaskProgress } from './task-progress';
import { WorkOrderChecksTable } from './work-order-checks-table';
import { WorkOrderIssuesTable } from './work-order-issues-table';
import { workOrderTaskLabel } from './work-order-task-label';

const workOrderFormSchema = z.object({
  asset: z
    .object({
      label: z.string(),
      value: z.string(),
      locationName: z.string().optional(),
    })
    .nullable(),
  status: z.nativeEnum(WorkOrderStatus).optional(),
  title: z.string().default(''),
  priority: z.nativeEnum(WorkOrderPriority).optional(),
  selectedIssues: z.array(z.string()).default([]),
  selectedChecks: z.array(z.string()).default([]),
  dueDate: z.date().optional(),
  assignedUsers: z.array(z.object({ label: z.string(), value: z.string(), avatarUrl: z.string().optional() })).default([]),
  instructions: z.string().default(''),
  poReference: z.string().default(''),
  comments: z.string().default(''),
  tags: z.array(z.object({ label: z.string(), value: z.string() })).default([]),
});

type WorkOrderFormInput = z.input<typeof workOrderFormSchema>;
type WorkOrderFormData = z.output<typeof workOrderFormSchema>;

type IssueWithSelection = IssueDTO & {
  selected: boolean;
};

interface WorkOrderDrawerProps {
  workOrderId?: string;
  asset?: AssetDTO | null;
  issue?: IssueDTO | null;
  check?: GetCheckResponseDTO | null;
  open?: boolean;
  onOpenChange?: (open: boolean, refresh?: boolean) => void;
  registerState?: RecordState;
  onCloseSuccess?: (workOrder: GetWorkOrdersResponseDTO['data'][number]) => void;
}

export default function WorkOrderDrawer({
  workOrderId,
  asset,
  issue,
  check,
  open: openProp,
  onOpenChange,
  registerState = RecordState.Active,
  onCloseSuccess,
}: WorkOrderDrawerProps) {
  const userTimezone = useUserStore.getState().user?.timezone;
  const toast = useToast();
  const queryClient = useQueryClient();
  const [createdWorkOrderId, setCreatedWorkOrderId] = useState<string | null>(null);
  const effectiveWorkOrderId = workOrderId || createdWorkOrderId;
  const { data: workOrderData, isFetching: isFetchingWorkOrder } = useQuery(getWorkOrderByIdQueryOptions({ workOrderId: effectiveWorkOrderId ?? undefined }));
  const workOrder = effectiveWorkOrderId ? workOrderData : undefined;
  const [view, setView] = useState<'form' | 'activity'>('form');
  const {
    data: woActionPages,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery(getWorkOrderActionsInfiniteQueryOptions({ workOrderId: effectiveWorkOrderId ?? undefined }));
  const woActions = woActionPages?.pages.flatMap((page) => page.data) ?? [];
  const { canEditWorkOrder } = usePermissions();
  const isArchived = registerState !== RecordState.Active;
  const canTakeDestructiveAction = workOrder ? canEditWorkOrder(workOrder.userId) : false;

  const [open, setOpen] = useState(openProp ?? false);
  const [loading, setLoading] = useState(false);
  const [issues, setIssues] = useState<IssueWithSelection[]>([]);
  const [checks, setChecks] = useState<GetChecksByAssetResponseDTO | null>(null);

  const [currentAsset, setCurrentAsset] = useState<AssetDTO | null>(null);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [selectedCheckId, setSelectedCheckId] = useState<string | null>(null);
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);
  const newlyCreatedIssueIdRef = useRef<string | null>(null);
  const [pendingUnlink, setPendingUnlink] = useState<{ type: 'issue' | 'check'; id: string; woRef: string } | null>(null);
  const [isUnlinking, setIsUnlinking] = useState(false);

  const { isConfirmOpen, confirmSafeToLeave, settleConfirm, onConfirmOpenChange } = useSaveChangesGuard(() => form.formState.isDirty);

  useEffect(() => {
    setOpen(openProp ?? false);
  }, [openProp]);

  useEffect(() => {
    if (effectiveWorkOrderId) {
      setLoading(true);
      return () => setLoading(false);
    }
  }, [effectiveWorkOrderId]);

  const { data: workOrderTags } = useQuery(getTagsQueryOptions({ entity: TagEntity.WorkOrder }));
  const workOrderTagOptions = useMemo(() => workOrderTags?.map((tag) => ({ label: tag.tag, value: tag.id })) ?? [], [workOrderTags]);

  const form = useForm<WorkOrderFormInput, unknown, WorkOrderFormData>({
    resolver: zodResolver(workOrderFormSchema),
    defaultValues: {
      asset: asset ? { value: asset.id, label: EntityRefUtil.createAssetLabel(asset) } : undefined,
      status: WorkOrderStatus.Open,
      selectedIssues: [],
      selectedChecks: [],
      assignedUsers: [],
      instructions: '',
      poReference: '',
      comments: '',
      tags: [],
    },
  });

  const noAssetSelected = !workOrderId && !form.watch('asset');

  const loadAssetData = async (assetId: string, selectedIssueIds?: string[], selectedCheckIds?: string[], silent?: boolean) => {
    if (!silent) setLoading(true);

    try {
      // Fetch the full asset data
      const assetData = await (await ApiService.GetAssetById(assetId)).json();
      setCurrentAsset(assetData.asset);

      // Fetch issues for this asset using v2 API
      const issuesResponse = await ApiService.GetIssuesByAsset({
        assetId,
        filter: {
          issueStatus: [IssueStatus.Open, IssueStatus.Closed],
        },
      });
      const issuesData = await issuesResponse.json();
      const currentSelectedIds = selectedIssueIds || form.getValues('selectedIssues') || [];
      const availableIssues: IssueWithSelection[] = issuesData.issues.map((i) => ({
        ...i,
        selected: currentSelectedIds.includes(i.id) || issue?.id === i.id,
      }));

      setIssues(availableIssues);

      // Update the form's selectedIssues to only include issues that are actually available
      const availableIssueIds = availableIssues.map((i) => i.id);
      const validSelectedIds = currentSelectedIds.filter((id) => availableIssueIds.includes(id));
      const finalSelectedIds = issue?.id && !validSelectedIds.includes(issue.id) ? [...validSelectedIds, issue.id] : validSelectedIds;

      form.setValue('selectedIssues', finalSelectedIds, { shouldDirty: false });

      // Fetch schedule checks for this asset
      try {
        const checksData = await (await ApiService.GetChecksByAsset({ assetId: assetId, filter: { status: [CheckStatus.Open, CheckStatus.Closed] } })).json();
        setChecks(checksData);

        // Sync selectedChecks with available checks
        const currentSelectedCheckIds = selectedCheckIds || form.getValues('selectedChecks') || [];
        const availableCheckIds = checksData?.checks?.map((c) => c.id) ?? [];
        const validSelectedCheckIds = currentSelectedCheckIds.filter((id) => availableCheckIds.includes(id));
        const finalSelectedCheckIds = check?.id && !validSelectedCheckIds.includes(check.id) ? [...validSelectedCheckIds, check.id] : validSelectedCheckIds;
        form.setValue('selectedChecks', finalSelectedCheckIds, { shouldDirty: false });
      } catch {
        console.error('Error loading schedule check data');
        toast.error('Failed to load schedule check data');
        setChecks(null);
      }
    } catch (error) {
      console.error('Error loading asset data:', error);
      toast.error('Failed to load asset data');
    } finally {
      setLoading(false);
    }
  };

  // Initialize form when opening
  useEffect(() => {
    if (workOrder) {
      form.reset({
        asset: {
          label: workOrder.asset ? EntityRefUtil.createAssetLabel(workOrder.asset) : '',
          value: workOrder.asset?.id,
          locationName: workOrder.asset?.location?.name,
        },
        status: workOrder.status,
        title: workOrder.title || '',
        priority: (workOrder.priority as WorkOrderPriority) ?? undefined,
        selectedIssues: workOrder.issues?.map((i) => i.id).sort() || [],
        selectedChecks: workOrder.checks?.map((c) => c.id).sort() || [],
        dueDate: workOrder.dueDate ? new Date(workOrder.dueDate) : undefined,
        assignedUsers:
          workOrder.gearbeltContributors?.map((u) => ({
            label: EntityRefUtil.createUserFullNameRef(u),
            value: u.id,
            avatarUrl: u.avatarUrl ?? undefined,
          })) || [],
        instructions: workOrder.instructions || '',
        poReference: workOrder.po || '',
        comments: workOrder.comments || '',
        tags: workOrder.tags?.map((tag) => ({ label: tag.tag, value: tag.id })) || [],
      });
      const pendingIssueId = newlyCreatedIssueIdRef.current;
      newlyCreatedIssueIdRef.current = null;

      if (workOrder.asset) {
        const selectedIssueIds = [...(workOrder.issues?.map((i) => i.id) || []), ...(pendingIssueId ? [pendingIssueId] : [])];
        loadAssetData(workOrder.asset.id, selectedIssueIds, workOrder.checks?.map((c) => c.id) || []).then(() => {
          if (pendingIssueId) {
            form.setValue('selectedIssues', [...selectedIssueIds].sort(), { shouldDirty: true });
          }
        });
      } else {
        setLoading(false);
      }
    } else {
      form.reset({
        asset: issue?.asset
          ? { label: EntityRefUtil.createAssetLabel(issue.asset), value: issue.asset.id, locationName: issue.asset.location?.name || issue.location?.name }
          : check?.asset
            ? { label: EntityRefUtil.createAssetLabel(check.asset), value: check.asset.id, locationName: check.asset.location?.name }
            : asset
              ? { label: EntityRefUtil.createAssetLabel(asset), value: asset.id, locationName: asset.location?.name }
              : null,
        status: undefined,
        title: '',
        priority: undefined,
        selectedIssues: issue ? [issue.id] : [],
        selectedChecks: check ? [check.id] : [],
        dueDate: undefined,
        assignedUsers: [],
        instructions: '',
        poReference: '',
        comments: '',
        tags: [],
      });

      if (issue?.asset) {
        loadAssetData(issue.asset.id, [issue.id]);
      } else if (check?.asset) {
        loadAssetData(check.asset.id, [], [check.id]);
      } else if (asset) {
        loadAssetData(asset.id);
      } else {
        setIssues([]);
        setChecks(null);
        setCurrentAsset(null);
      }
    }
  }, [workOrder, form, asset, issue, check]); // eslint-disable-line react-hooks/exhaustive-deps

  const { mutateAsync: saveWorkOrder, isPending: isSaving } = useMutation({
    mutationFn: async (data: WorkOrderFormData) => {
      const payload = {
        assetId: data.asset!.value,
        dueDate: data.dueDate,
        comments: data.comments,
        instructions: data.instructions,
        po: data.poReference,
        title: data.title || undefined,
        priority: data.priority,
        status: data.status || WorkOrderStatus.Open,
        issues: data.selectedIssues,
        checkIds: data.selectedChecks,
        assigned: data.assignedUsers.map((u) => u.value),
        tags: data.tags.map((tag) => tag.label),
      };

      if (workOrder) {
        return ApiService.UpdateWorkOrder({
          workOrderId: workOrder.id,
          ...payload,
          dueDate: data.dueDate ? data.dueDate.toISOString() : undefined,
        });
      } else {
        return ApiService.CreateWorkOrder({ ...payload, dueDate: data.dueDate ? data.dueDate.toISOString() : undefined });
      }
    },
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: awaWorkOrderKeys.all });
      // Refetch this drawer's own detail query directly, then reset the rest of
      // workOrderKeys.all (the register list included) excluding it — resetting
      // the detail query in the same sweep clears `workOrder` to undefined
      // mid-render, unmounting the drawer body while it's still closing.
      await queryClient.refetchQueries({ queryKey: workOrderKeys.detail(effectiveWorkOrderId ?? undefined) });
      await queryClient.resetQueries({
        queryKey: workOrderKeys.all,
        predicate: (query) => !(query.queryKey[1] === 'detail' && query.queryKey[2] === effectiveWorkOrderId),
      });
      toast.success(workOrder ? 'Work order updated successfully' : 'Work order created successfully');
      if (workOrder && workOrder.status === WorkOrderStatus.Open && variables.status === WorkOrderStatus.Closed) {
        onCloseSuccess?.({ ...workOrder, instructions: variables.instructions ?? workOrder.instructions });
      }
    },
    onError: (error) => {
      toast.error(`Error ${workOrder ? 'updating' : 'creating'} work order: ${error.message}`);
    },
  });

  const { mutateAsync: updateWorkOrderState, isPending: isUpdatingState } = useMutation({
    mutationFn: async (state: RecordState) => {
      if (!workOrder) throw new Error('No work order to update');
      return ApiService.BulkUpdateWorkOrderState({ ids: [workOrder.id], state });
    },
    onSuccess: (_, state) => {
      queryClient.invalidateQueries({ queryKey: awaWorkOrderKeys.all });
      queryClient.invalidateQueries({ queryKey: workOrderKeys.all });
      const messages = {
        [RecordState.Archived]: 'Work order archived successfully',
        [RecordState.Deleted]: 'Work order deleted successfully',
        [RecordState.Active]: 'Work order restored successfully',
      };
      toast.success(messages[state]);
      setShowArchiveConfirm(false);
      setShowDeleteConfirm(false);
      setShowRestoreConfirm(false);
      handleOpenChange(false, true);
    },
    onError: (error) => {
      toast.error(`Error updating work order: ${error.message}`);
    },
  });

  async function closeDrawer(refresh?: boolean) {
    if (!(await confirmSafeToLeave())) return;
    handleOpenChange(false, refresh);
  }

  const handleOpenChange = (open: boolean, refresh?: boolean) => {
    onOpenChange?.(open, refresh);
    setOpen(open);
    if (!open) {
      setView('form');
      form.reset({
        asset: null,
        status: WorkOrderStatus.Open,
        title: '',
        priority: undefined,
        selectedIssues: [],
        selectedChecks: [],
        assignedUsers: [],
        instructions: '',
        poReference: '',
        comments: '',
        tags: [],
      });
      setIssues([]);
      setChecks(null);
      setCurrentAsset(null);
      setSelectedCheckId(null);
      setSelectedIssueId(null);
      setCreatedWorkOrderId(null);
      setLoading(false);
    }
  };

  const handleSubmit = async (data: WorkOrderFormData) => {
    // Validation
    if (!data.asset) {
      toast.error('Please select an asset');
      return;
    }

    if (data.status === WorkOrderStatus.Closed) {
      const hasOpenIssues = issues.filter((i) => data.selectedIssues.includes(i.id)).some((i) => i.status !== IssueStatus.Closed);

      if (hasOpenIssues) {
        toast.error('Cannot close a work order with open tasks');
        return;
      }
    }

    await saveWorkOrder(data);
    handleOpenChange(false, true);
  };

  const handleArchiveConfirm = async () => {
    await updateWorkOrderState(RecordState.Archived);
  };

  const handleDeleteConfirm = async () => {
    await updateWorkOrderState(RecordState.Deleted);
  };

  const handleRestoreConfirm = async () => {
    await updateWorkOrderState(RecordState.Active);
  };

  const toggleIssueSelection = (issueId: string) => {
    const currentSelected = form.getValues('selectedIssues') ?? [];
    const newSelected = currentSelected.includes(issueId) ? currentSelected.filter((id) => id !== issueId) : [...currentSelected, issueId];

    form.setValue('selectedIssues', newSelected.sort(), { shouldDirty: true });
    setIssues((prev) => prev.map((i) => (i.id === issueId ? { ...i, selected: !i.selected } : i)));
  };

  const statusOptions = useMemo(
    () => [
      {
        value: WorkOrderStatus.Open,
        label: (
          <Badge className="text-xs" variant={'grey'}>
            Open
          </Badge>
        ),
      },
      {
        value: WorkOrderStatus.Closed,
        label: (
          <Badge className="text-xs" variant={'success'}>
            Closed
          </Badge>
        ),
      },
    ],
    [],
  );

  const priorityOptions = useMemo(
    () =>
      Object.values(WorkOrderPriority).map((value) => {
        const colorClass = getWorkOrderPriorityColorClass(value);
        const icon =
          value === WorkOrderPriority.Critical ? (
            <WarningIcon size={20} className="text-default-red" />
          ) : (
            <div className="grid size-4 place-items-center">
              <div className={cn('mx-[3px] h-[15px] w-[15px] rounded-full border-[3px]', colorClass)} />
            </div>
          );
        return {
          value,
          label: (
            <div className="flex items-center gap-1 leading-none">
              {icon}
              {startCase(value)}
            </div>
          ),
        };
      }),
    [],
  );

  const filteredChecks = useMemo(() => {
    return (
      checks?.checks?.filter((check) => {
        if (check.schedule?.state !== RecordState.Active) {
          return check.status === CheckStatus.Closed && check.workOrder?.id === workOrder?.id;
        }
        return true;
      }) ?? []
    );
  }, [checks, workOrder?.id]);

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(newOpen) => {
          if (!newOpen) closeDrawer();
        }}>
        {!workOrderId && (
          <SheetTrigger asChild>
            <TablePageHeaderActionButton icon={<PlusIcon />} label="Create Work Order" variant="success" kind="primary" onClick={() => setOpen(true)} />
          </SheetTrigger>
        )}
        <SheetContent className="flex flex-col" hideCloseIcon>
          <SheetHeader className="border-grey-3 h-[64px] min-h-[64px] flex-row items-center justify-between space-y-0 border-b bg-white">
            <SheetTitle>
              {isFetchingWorkOrder && !form.formState.isDirty ? 'Loading work order details...' : workOrder ? 'Work order record' : 'Create Work Order'}
            </SheetTitle>
            <div className="flex items-center gap-2">
              {workOrder && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setView((prev) => (prev === 'activity' ? 'form' : 'activity'))}>
                  <ActivityIcon className="h-4 w-4" />
                  {view === 'activity' ? 'Hide Activity' : 'Show Activity'}
                </Button>
              )}
              {workOrder && <SingleRecordPdfControls recordType="work-order" recordId={workOrder.id} documentUrl={undefined} />}
              {workOrder && registerState === RecordState.Active && canTakeDestructiveAction && (
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label="Archive"
                        onClick={() => setShowArchiveConfirm(true)}
                        disabled={loading || isSaving || isUpdatingState}>
                        <ArchiveIcon className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipPortal>
                      <TooltipContent className="bg-default-text pointer-events-none border-none text-xs text-white">
                        <TooltipArrow className="fill-default-text" />
                        Archive
                      </TooltipContent>
                    </TooltipPortal>
                  </Tooltip>
                </TooltipProvider>
              )}
              {workOrder && registerState === RecordState.Archived && canTakeDestructiveAction && (
                <>
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label="Delete"
                          onClick={() => setShowDeleteConfirm(true)}
                          disabled={loading || isSaving || isUpdatingState}>
                          <DeleteIcon className="text-default-red h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipPortal>
                        <TooltipContent className="bg-default-text pointer-events-none border-none text-xs text-white">
                          <TooltipArrow className="fill-default-text" />
                          Delete
                        </TooltipContent>
                      </TooltipPortal>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label="Restore"
                          onClick={() => setShowRestoreConfirm(true)}
                          disabled={loading || isSaving || isUpdatingState}>
                          <UndoIcon className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipPortal>
                        <TooltipContent className="bg-default-text pointer-events-none border-none text-xs text-white">
                          <TooltipArrow className="fill-default-text" />
                          Restore
                        </TooltipContent>
                      </TooltipPortal>
                    </Tooltip>
                  </TooltipProvider>
                </>
              )}
              {form.watch('asset') && (
                <Button
                  type="submit"
                  variant="success"
                  disabled={loading || isSaving || !form.formState.isDirty}
                  loading={isSaving}
                  onClick={form.handleSubmit(handleSubmit)}>
                  Save
                </Button>
              )}
              <button
                type="button"
                className="data-[state=open]:bg-default-blue cursor-pointer rounded-sm opacity-70 transition-opacity outline-none hover:opacity-100 disabled:pointer-events-none"
                disabled={form.formState.isSubmitting}
                onClick={() => closeDrawer()}>
                <CloseIcon className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </button>
            </div>
          </SheetHeader>
          {view === 'activity' && workOrderId && (
            <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-4">
              <div>Activity</div>
              <div className="border-b" />
              <ActivityTimeline
                actions={woActions}
                resolveLabel={workOrderTaskLabel}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                fetchNextPage={fetchNextPage}
              />
            </div>
          )}
          {view === 'form' && workOrderId && (
            <div className="flex flex-col gap-1 px-4 pt-4">
              {loading || isFetchingWorkOrder ? (
                <div className="flex items-end justify-between gap-2 text-sm">
                  <Skeleton className="h-[21px] w-32" />
                  <div className="flex items-center gap-2">
                    <div className="flex flex-col items-end gap-0.5">
                      <Skeleton className="h-[21px] w-28" />
                      <Skeleton className="h-[21px] w-32" />
                    </div>
                    <Skeleton className="size-7 rounded-full" />
                  </div>
                </div>
              ) : (
                workOrder &&
                (() => {
                  const displayName = EntityRefUtil.createUserFullNameRef(workOrder.user, '-');
                  const creatorAvatarUrl = workOrder.sitemateUsers?.find((su) => su.id === workOrder.user?.id)?.avatarUrl || workOrder.user?.avatarUrl;
                  return (
                    <div className="flex items-end justify-between gap-2 text-sm">
                      <div>
                        <span className="font-medium">ID: </span> {EntityRefUtil.createWorkOrderRef(workOrder.number)}
                      </div>
                      <div className="flex items-center gap-2 text-right">
                        <div className="flex flex-col gap-0.5">
                          <div className="line-clamp-1 font-medium">{displayName}</div>
                          <div className="text-secondary-text">
                            {formatManuallyWithUserTimezone(new Date(workOrder.createdAt), 'MMM DD, YYYY hh:mm a', userTimezone)}
                          </div>
                        </div>
                        <Avatar>
                          {creatorAvatarUrl ? (
                            <AvatarImage src={creatorAvatarUrl} alt={displayName} />
                          ) : (
                            <AvatarFallback>{initials(displayName)}</AvatarFallback>
                          )}
                        </Avatar>
                      </div>
                    </div>
                  );
                })()
              )}
              <div className={cn('border-b-4', loading || isFetchingWorkOrder ? 'border-grey-2' : 'border-default-blue')} />
            </div>
          )}
          {view === 'form' &&
            (loading || (isFetchingWorkOrder && !form.formState.isDirty) ? (
              <WorkOrderDrawerSkeleton showAssetField={!asset} isExisting={!!workOrderId} />
            ) : (
              <div className="flex-1 overflow-y-auto">
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(handleSubmit)}>
                    <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-6 pt-4 pb-6">
                      {/* Asset Selection */}
                      {!asset && (
                        <>
                          {workOrder ? (
                            <div className="flex flex-col gap-1">
                              <Label>Asset</Label>
                              <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] flex-wrap items-center gap-1 rounded p-2 ring-2 ring-inset">
                                <Link className="text-default-blue text-sm underline" to={getWorkspaceRoute(`/assets/${workOrder.asset?.id}`)}>
                                  {workOrder.asset ? EntityRefUtil.createAssetLabel(workOrder.asset) : 'No asset'}
                                </Link>
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-col gap-1">
                              <Label>Asset</Label>
                              <Controller
                                control={form.control}
                                name="asset"
                                render={({ field: { value, onChange } }) => (
                                  <AsyncSelect<{ label: string; value: string; locationName?: string }>
                                    cacheOptions
                                    defaultOptions
                                    value={value}
                                    onChange={(selected) => {
                                      if (selected) {
                                        form.reset({
                                          asset: selected,
                                          status: undefined,
                                          title: '',
                                          priority: undefined,
                                          selectedIssues: [],
                                          selectedChecks: [],
                                          dueDate: undefined,
                                          assignedUsers: [],
                                          instructions: '',
                                          poReference: '',
                                          comments: '',
                                          tags: [],
                                        });
                                        loadAssetData(selected.value);
                                      } else {
                                        onChange(null);
                                        setIssues([]);
                                        setChecks(null);
                                        setCurrentAsset(null);
                                      }
                                    }}
                                    loadOptions={async (input: string) => {
                                      const { data } = await (
                                        await ApiService.GetAssets({
                                          limit: 100,
                                          filters: {
                                            search: input,
                                            state: RecordState.Active,
                                          },
                                        })
                                      ).json();
                                      return (
                                        data?.map((asset) => ({
                                          value: asset.id,
                                          label: EntityRefUtil.createAssetLabel(asset),
                                          locationName: asset.location?.name,
                                        })) ?? []
                                      );
                                    }}
                                    classNames={SELECT_STYLES()}
                                    className={cn('w-full')}
                                    blurInputOnSelect
                                    isClearable
                                    isDisabled={isSaving || loading}
                                    placeholder="Search asset..."
                                    aria-label="Asset"
                                  />
                                )}
                              />
                            </div>
                          )}
                        </>
                      )}

                      {/* Status (for existing work orders) */}
                      {workOrder && (
                        <Controller
                          control={form.control}
                          name="status"
                          render={({ field: { value, onChange } }) => (
                            <div id="status-select" className="flex flex-col gap-1">
                              <Label>Status</Label>
                              <Select<{ value: WorkOrderStatus; label: React.ReactNode }>
                                value={statusOptions.find((option) => option.value === value) ?? undefined}
                                onChange={(val) => onChange(val?.value)}
                                options={statusOptions}
                                classNames={SELECT_STYLES()}
                                styles={{
                                  control: (provided, state) => ({
                                    ...provided,
                                    backgroundColor: state.isDisabled ? 'rgb(var(--grey-1))' : provided.backgroundColor,
                                    opacity: '1',
                                  }),
                                }}
                                className={cn('w-full')}
                                blurInputOnSelect
                                placeholder="Select Status..."
                                isDisabled={isSaving || loading || noAssetSelected}
                              />
                            </div>
                          )}
                        />
                      )}

                      {/* Title */}
                      <div className="flex flex-col gap-1">
                        <Label>Title</Label>
                        <Controller
                          control={form.control}
                          name="title"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input {...field} placeholder="Enter work order title..." disabled={isSaving || loading || noAssetSelected} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Task Progress */}
                      {(() => {
                        const selectedIssueList = issues.filter((i) => i.selected);
                        const selectedCheckIds = form.watch('selectedChecks') ?? [];
                        const selectedCheckList = filteredChecks.filter((c) => selectedCheckIds.includes(c.id));
                        const total = selectedIssueList.length + selectedCheckList.length;
                        const closedIssues = selectedIssueList.filter((i) => i.status === IssueStatus.Closed).length;
                        const closedChecks = selectedCheckList.filter((c) => c.status === CheckStatus.Closed).length;
                        const completed = closedIssues + closedChecks;
                        const openIssueCount = selectedIssueList.length - closedIssues;
                        const openCheckCount = selectedCheckList.length - closedChecks;
                        return (
                          <div className="mb-1 flex flex-col gap-1">
                            <Label>Progress</Label>
                            <TaskProgress completed={completed} total={total} openIssueCount={openIssueCount} openCheckCount={openCheckCount} />
                          </div>
                        );
                      })()}

                      {/* Issues Table */}
                      <WorkOrderIssuesTable
                        issues={issues}
                        workOrderId={workOrder?.id}
                        workOrderNumber={workOrder?.number}
                        workOrderStatus={workOrder?.status}
                        isArchived={isArchived}
                        onToggleIssue={(issueId) => {
                          const issue = issues.find((i) => i.id === issueId);
                          const isLinkedToOtherWo = !!issue?.workorderId && issue.workorderId !== workOrder?.id;
                          if (isLinkedToOtherWo) {
                            const woRef = issue?.workOrder?.number ? EntityRefUtil.createWorkOrderRef(issue.workOrder.number) : 'another work order';
                            setPendingUnlink({ type: 'issue', id: issueId, woRef });
                            return;
                          }
                          toggleIssueSelection(issueId);
                        }}
                        onToggleAll={(candidateIds, allCurrentlySelected) => {
                          const idSet = new Set(candidateIds);
                          if (allCurrentlySelected) {
                            const next = (form.getValues('selectedIssues') ?? []).filter((id) => !idSet.has(id)).sort();
                            form.setValue('selectedIssues', next, { shouldDirty: true });
                            setIssues((prev) => prev.map((i) => (idSet.has(i.id) ? { ...i, selected: false } : i)));
                          } else {
                            const existing = new Set(form.getValues('selectedIssues') ?? []);
                            idSet.forEach((id) => existing.add(id));
                            form.setValue('selectedIssues', Array.from(existing).sort(), { shouldDirty: true });
                            setIssues((prev) => prev.map((i) => (idSet.has(i.id) ? { ...i, selected: true } : i)));
                          }
                        }}
                        onIssueClick={async (issueId) => {
                          if (workOrder && !(await confirmSafeToLeave())) return;
                          setOpen(false);
                          setSelectedIssueId(issueId);
                        }}
                        onCreateIssue={async () => {
                          if (workOrder && !(await confirmSafeToLeave())) return;
                          setOpen(false);
                          setSelectedIssueId('create');
                        }}
                        hasAsset={!!(workOrder?.asset || currentAsset || form.watch('asset'))}
                      />

                      {/* Schedule Checks Table */}
                      <WorkOrderChecksTable
                        checks={filteredChecks}
                        selectedCheckIds={checks ? (form.watch('selectedChecks') ?? []) : []}
                        workOrderId={workOrder?.id}
                        workOrderNumber={workOrder?.number}
                        workOrderStatus={workOrder?.status}
                        isArchived={isArchived}
                        onToggleCheck={(checkId) => {
                          const check = filteredChecks.find((c) => c.id === checkId);
                          const isLinkedToOtherWo = !!check?.workOrder && check.workOrder.id !== workOrder?.id;
                          if (isLinkedToOtherWo) {
                            const woRef = check?.workOrder?.number ? EntityRefUtil.createWorkOrderRef(check.workOrder.number) : 'another work order';
                            setPendingUnlink({ type: 'check', id: checkId, woRef });
                            return;
                          }
                          const current = form.getValues('selectedChecks') ?? [];
                          if (current.includes(checkId)) {
                            form.setValue('selectedChecks', current.filter((id) => id !== checkId).sort(), { shouldDirty: true });
                          } else {
                            form.setValue('selectedChecks', [...current, checkId].sort(), { shouldDirty: true });
                          }
                        }}
                        onToggleAll={(candidateIds, allCurrentlySelected) => {
                          const idSet = new Set(candidateIds);
                          const currentCheckIds = form.getValues('selectedChecks') ?? [];
                          if (allCurrentlySelected) {
                            form.setValue('selectedChecks', currentCheckIds.filter((id) => !idSet.has(id)).sort(), { shouldDirty: true });
                          } else {
                            const next = new Set(currentCheckIds);
                            idSet.forEach((id) => next.add(id));
                            form.setValue('selectedChecks', Array.from(next).sort(), { shouldDirty: true });
                          }
                        }}
                        onCheckClick={async (check) => {
                          if (workOrder && !(await confirmSafeToLeave())) return;
                          setOpen(false);
                          setSelectedCheckId(check.id);
                        }}
                        hasAsset={!!(workOrder?.asset || currentAsset || form.watch('asset'))}
                      />

                      {/* Users */}
                      <div className="flex flex-col gap-1">
                        <Label>Assigned to Gearbelt contributor</Label>
                        <Controller
                          control={form.control}
                          name="assignedUsers"
                          render={({ field: { value, onChange } }) => (
                            <AsyncMultiSelect<{ label: string; value: string; avatarUrl?: string }>
                              defaultOptions
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              blurInputOnSelect={false}
                              loadOptions={(input) => loadUserOptions(input, 'contributors', () => toast.error('Failed to load users'))}
                              formatOptionLabel={(option) => (
                                <div className="flex items-center gap-2">
                                  <Avatar className="h-5 w-5">
                                    {option.avatarUrl ? (
                                      <AvatarImage src={option.avatarUrl} alt={option.label} />
                                    ) : (
                                      <AvatarFallback className="text-[10px]">{initials(option.label)}</AvatarFallback>
                                    )}
                                  </Avatar>
                                  <span>{option.label}</span>
                                </div>
                              )}
                              value={value}
                              onChange={(val) => onChange(val || [])}
                              styles={{
                                control: (base) => ({ ...base, paddingTop: '4px', paddingBottom: '4px', minHeight: '41px' }),
                                multiValue: (base) => ({ ...base, paddingTop: '3px', paddingBottom: '3px' }),
                                option: (base, state) => ({
                                  ...base,
                                  ...(state.isSelected && { backgroundColor: 'var(--color-default-light-blue)' }),
                                }),
                              }}
                              className={cn('w-full')}
                              isDisabled={noAssetSelected}
                              placeholder="Select users..."
                            />
                          )}
                        />
                      </div>

                      {/* Assigned to Sitemate user (legacy) — read-only, only when legacy assignees exist */}
                      {!!workOrder?.sitemateUsers?.length && (
                        <div className="flex flex-col gap-1">
                          <Label>Assigned to Sitemate user (legacy)</Label>
                          <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] flex-wrap items-center gap-2 rounded p-2 ring-2 ring-inset">
                            {workOrder.sitemateUsers.map((u) => {
                              const name = EntityRefUtil.createUserFullNameRef(u);
                              return (
                                <div key={u.id} className="flex items-center gap-1.5">
                                  <Avatar className="h-5 w-5">
                                    {u.avatarUrl ? (
                                      <AvatarImage src={u.avatarUrl} alt={name} />
                                    ) : (
                                      <AvatarFallback className="text-[10px]">{initials(name)}</AvatarFallback>
                                    )}
                                  </Avatar>
                                  <span className="text-sm">{name}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Priority */}
                      <div className="flex flex-col gap-1">
                        <Label>Priority</Label>
                        <Controller
                          control={form.control}
                          name="priority"
                          render={({ field: { value, onChange } }) => (
                            <Select<{ label: ReactNode; value: WorkOrderPriority }>
                              value={priorityOptions.find((option) => option.value === value) ?? undefined}
                              onChange={(val) => onChange(val?.value ?? null)}
                              options={priorityOptions}
                              classNames={SELECT_STYLES()}
                              className={cn('w-full')}
                              blurInputOnSelect
                              isClearable
                              placeholder="Select priority..."
                              isDisabled={isSaving || loading || noAssetSelected}
                            />
                          )}
                        />
                      </div>

                      {/* Due Date */}
                      <div className="flex flex-col gap-1">
                        <Label>Due Date</Label>
                        <Controller
                          control={form.control}
                          name="dueDate"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <DatePicker
                                  date={field.value}
                                  setDate={field.onChange}
                                  disabled={isSaving || isFetchingWorkOrder || noAssetSelected}
                                  className="w-full"
                                  placeholder="Select due date..."
                                  isClearable
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Instructions */}
                      <div className="flex flex-col gap-1">
                        <Label>Instructions</Label>
                        <Controller
                          control={form.control}
                          name="instructions"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Textarea {...field} placeholder="Enter work order details" rows={2} disabled={isSaving || loading || noAssetSelected} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* PO Reference */}
                      <div className="flex flex-col gap-1">
                        <Label>PO Reference</Label>
                        <Controller
                          control={form.control}
                          name="poReference"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input {...field} placeholder="Purchase Order #" disabled={isSaving || loading || noAssetSelected} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Comments */}
                      <div className="flex flex-col gap-1">
                        <Label>Comments</Label>
                        <Controller
                          control={form.control}
                          name="comments"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Textarea {...field} placeholder="Enter comments" rows={3} disabled={isSaving || loading || noAssetSelected} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Tags */}
                      <div className="flex flex-col gap-1">
                        <Label>Work Order Tags</Label>
                        <Controller
                          control={form.control}
                          name="tags"
                          render={({ field: { value, onChange } }) => (
                            <CreatableMultiSelect<{ label: string; value: string }>
                              options={workOrderTagOptions}
                              value={value}
                              onChange={(selected) => onChange(selected ? [...selected] : [])}
                              placeholder="Select Tags..."
                              menuPlacement="auto"
                              chipVariant="sky"
                              isDisabled={isSaving || loading || noAssetSelected}
                            />
                          )}
                        />
                      </div>

                      {workOrder?.asset && (
                        <ReadOnlyField label="Asset Tags">
                          <TagBadges tags={workOrder.asset.tags} />
                        </ReadOnlyField>
                      )}
                    </div>
                  </form>
                </Form>
              </div>
            ))}
        </SheetContent>
      </Sheet>

      <CheckDrawer
        checkId={selectedCheckId}
        open={!!selectedCheckId}
        onOpenChange={(isOpen, success) => {
          if (!isOpen) {
            setSelectedCheckId(null);
            setOpen(true);
          }
          if (success) {
            queryClient.invalidateQueries({ queryKey: workOrderKeys.all });
            // The checks register renders from imperative state, not a query, and the
            // work-order refetch alone doesn't reload it when the WO payload is unchanged.
            const reloadAssetId = workOrder?.asset?.id ?? currentAsset?.id;
            if (reloadAssetId) {
              loadAssetData(reloadAssetId, undefined, undefined, true);
            }
          }
        }}
        parentWorkOrderId={workOrder?.id}
      />

      <IssueDrawer
        issueId={selectedIssueId === 'create' ? undefined : (selectedIssueId ?? undefined)}
        asset={currentAsset ?? undefined}
        open={!!selectedIssueId}
        noTrigger
        source={IssueSource.Manual}
        onOpenChange={(isOpen, success) => {
          if (!isOpen) {
            setSelectedIssueId(null);
            setOpen(true);
            if (success) {
              if (!workOrder && currentAsset?.id) {
                setLoading(true);
                loadAssetData(currentAsset.id, form.getValues('selectedIssues'), undefined, true);
              } else {
                queryClient.invalidateQueries({ queryKey: workOrderKeys.all });
                // The issues register renders from imperative state, not a query, and the
                // work-order refetch alone doesn't reload it when the WO payload is unchanged.
                const reloadAssetId = workOrder?.asset?.id ?? currentAsset?.id;
                if (reloadAssetId) {
                  loadAssetData(reloadAssetId, undefined, undefined, true);
                }
              }
            }
          }
        }}
        callback={
          selectedIssueId === 'create'
            ? (createdIssue) => {
                newlyCreatedIssueIdRef.current = createdIssue.id;
                const currentSelected = form.getValues('selectedIssues') ?? [];
                if (!currentSelected.includes(createdIssue.id)) {
                  form.setValue('selectedIssues', [...currentSelected, createdIssue.id].sort(), { shouldDirty: true });
                }
              }
            : undefined
        }
      />

      <ArchiveConfirmationDialog
        open={showArchiveConfirm}
        onOpenChange={setShowArchiveConfirm}
        callback={handleArchiveConfirm}
        loading={isUpdatingState}
        title="Archive work order?"
        description="Are you sure you want to archive this work order? Open tasks will be removed from it. You can restore it later from the archived tab."
      />
      <DeleteConfirmationDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        callback={handleDeleteConfirm}
        loading={isUpdatingState}
        title="Delete work order?"
        description="Are you sure you want to delete this work order? This action cannot be undone."
      />
      <RestoreConfirmationDialog
        open={showRestoreConfirm}
        onOpenChange={setShowRestoreConfirm}
        callback={handleRestoreConfirm}
        loading={isUpdatingState}
        title="Restore work order?"
        description="Are you sure you want to restore this work order? It will be moved back to the active tab."
      />
      <RemoveConfirmationDialog
        open={!!pendingUnlink}
        onOpenChange={(open) => {
          if (!open) setPendingUnlink(null);
        }}
        callback={async () => {
          if (!pendingUnlink) return;
          setIsUnlinking(true);
          try {
            if (pendingUnlink.type === 'issue') {
              await ApiService.UnlinkIssue(pendingUnlink.id);
              setIssues((prev) => prev.map((i) => (i.id === pendingUnlink.id ? { ...i, workorderId: null, workOrder: null } : i)));
            } else {
              await ApiService.UnlinkCheck(pendingUnlink.id);
              setChecks((prev) =>
                prev ? { ...prev, checks: prev.checks?.map((c) => (c.id === pendingUnlink.id ? { ...c, workOrder: null } : c)) ?? prev.checks } : prev,
              );
            }
            queryClient.invalidateQueries({ queryKey: workOrderKeys.all });
            setPendingUnlink(null);
          } catch {
            toast.error(`Failed to remove ${pendingUnlink.type} from work order`);
          } finally {
            setIsUnlinking(false);
          }
        }}
        loading={isUnlinking}
        title="Remove from work order?"
        description={`This will remove the ${pendingUnlink?.type ?? 'item'} from ${pendingUnlink?.woRef ?? 'the work order'}. It will become available for other work orders.`}
      />
      <SaveChangesDialog
        open={isConfirmOpen}
        onOpenChange={onConfirmOpenChange}
        saveCallback={form.handleSubmit(async (values) => {
          try {
            const response = await saveWorkOrder(values);
            if (!workOrder) {
              const data = await response.json();
              setCreatedWorkOrderId(data.workOrder.id);
            }
            form.reset(values);
            settleConfirm(true);
          } catch {
            // mutation's onError already surfaced the toast
          }
        })}
        discardCallback={() => {
          form.reset();
          settleConfirm(true);
        }}
        loading={isSaving}
        title="Save changes?"
        description="Do you want to save your changes before leaving?"
      />
    </>
  );
}

function WorkOrderDrawerSkeleton({ showAssetField, isExisting }: { showAssetField: boolean; isExisting: boolean }) {
  return (
    <div className="flex flex-col gap-2 px-6 pt-4 pb-6">
      {showAssetField && (
        <div className="flex flex-col gap-1">
          <Label>Asset</Label>
          <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] items-center rounded p-2 ring-2 ring-inset">
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      )}
      {isExisting && (
        <div className="flex flex-col gap-1">
          <Label>Status</Label>
          <div className="border-grey-3 flex items-center rounded border-2 bg-white px-3 py-2">
            <Skeleton className="h-[21px] w-12 rounded" />
          </div>
        </div>
      )}
      <div className="flex flex-col gap-1">
        <Label>Title</Label>
        <div className="border-grey-3 flex h-[41px] items-center rounded border-2 bg-white p-2">
          <Skeleton className="h-4 w-1/2" />
        </div>
      </div>
      <div className="mb-1 flex flex-col gap-1">
        <Label>Progress</Label>
        <div className="flex h-5 w-full items-center gap-1">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-3 flex-1 rounded-full" />
          ))}
        </div>
      </div>
      <WorkOrderIssuesTable
        issues={[]}
        hasAsset={false}
        isArchived={false}
        loading
        onToggleIssue={() => {}}
        onToggleAll={() => {}}
        onIssueClick={() => {}}
        onCreateIssue={() => {}}
      />
      <WorkOrderChecksTable
        checks={[]}
        selectedCheckIds={[]}
        hasAsset={false}
        isArchived={false}
        loading
        onToggleCheck={() => {}}
        onToggleAll={() => {}}
        onCheckClick={() => {}}
      />
      <div className="flex flex-col gap-1">
        <Label>Users</Label>
        <div className="border-grey-3 flex min-h-[41px] items-center gap-3 rounded border-2 bg-white px-3 py-2">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center gap-1">
              <Skeleton className="size-5 shrink-0 rounded-full" />
              <Skeleton className="h-4 w-24" />
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <Label>Priority</Label>
        <div className="border-grey-3 flex min-h-[41px] items-center rounded border-2 bg-white px-3 py-2">
          <Skeleton className="h-4 w-1/4" />
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <Label>Due Date</Label>
        <div className="border-grey-3 flex h-10 items-center rounded border-2 bg-white p-2">
          <Skeleton className="h-4 w-1/3" />
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <Label>Instructions</Label>
        <div className="border-grey-3 bg-grey-1 flex h-20 items-start rounded border-2 p-2">
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <Label>PO Reference</Label>
        <div className="border-grey-3 flex h-[41px] items-center rounded border-2 bg-white p-2">
          <Skeleton className="h-4 w-1/3" />
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <Label>Comments</Label>
        <div className="border-grey-3 bg-grey-1 flex h-20 items-start rounded border-2 p-2">
          <Skeleton className="h-4 w-1/2" />
        </div>
      </div>
    </div>
  );
}
